import { SuperDrawerContent, SuperDrawerHeader, TabsWithContent, useSessionStorage } from "@homebound/beam";
import { ProjectItemFilter } from "src/generated/graphqlTypes";
import { useBudgetSuperDrawerQuery } from "src/routes/projects/budget/components/BudgetSuperDrawer.generated";
import { queryResult, RequireAtLeastOne } from "src/utils";
import { BudgetActivityTab } from "./tabs/BudgetActivityTab";
import { BudgetCommentsTab } from "./tabs/BudgetCommentsTab";
import { BudgetDetailsTab } from "./tabs/BudgetDetailsTab";

export type BudgetSuperDrawerProps = RequireAtLeastOne<{ projectItemIds: string[]; costCodeIds: string[] }> & {
  title: string;
  projectId: string;
  isPiRow: boolean;
};

export function BudgetSuperDrawer(props: BudgetSuperDrawerProps) {
  const { title, projectId, isPiRow } = props;
  const [selectedTab, setSelectedTab] = useSessionStorage("budgetSuperDrawerTab", "details");
  const query = useBudgetSuperDrawerQuery({ variables: { filter: mapToFilter(props), projectId } });

  return queryResult(query, {
    data: ({ projectItems }) => {
      const changeEventIds = projectItems
        .flatMap((pi) => pi.changeEventLineItems)
        .map((celi) => celi.changeEvent.id)
        .unique();
      const commitmentLikeIds = projectItems
        .flatMap((pi) => pi.commitmentLineItems)
        .map((li) => li.owner.id)
        .unique();
      const billIds = projectItems
        .flatMap((pi) => pi.billLineItems)
        .map((bli) => bli.bill.id)
        .unique();

      const detailsTab = {
        name: "Details",
        value: "details",
        render: () => <BudgetDetailsTab projectItems={projectItems} projectId={projectId} />,
      };

      const commentTab = {
        name: "Comments",
        value: "comments",
        // The comment tab in the budget super drawer should only display
        // when clicking on a single project item row, so we can safely assume
        // that we are referring to the first and only project item in our drawer query result
        // as the commentable entity
        render: () => <BudgetCommentsTab commentable={projectItems[0]} />,
      };

      const activityTab = {
        name: "Activity",
        value: "activity",
        render: () => (
          <BudgetActivityTab
            projectId={projectId}
            // Restrict activity feed to entities existing on the current selected cost code group or project item row
            parentIds={[...commitmentLikeIds, ...billIds, ...changeEventIds]}
          />
        ),
      };

      return (
        <SuperDrawerContent>
          <SuperDrawerHeader title={title} />
          <TabsWithContent
            tabs={[detailsTab, ...(isPiRow ? [commentTab] : []), activityTab]}
            selected={selectedTab}
            onChange={setSelectedTab}
          />
        </SuperDrawerContent>
      );
    },
  });
}

function mapToFilter(props: BudgetSuperDrawerProps): ProjectItemFilter {
  return {
    project: [props.projectId],
    id: props.projectItemIds ? props.projectItemIds : undefined,
    costCode: props.costCodeIds ? props.costCodeIds : undefined,
  };
}
